import {
  download,
  type WorkspaceArchiveProgress,
  WorkspaceYDocArchiveWriter,
} from '@blocksuite/affine/widgets/linked-doc';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';

import type { WorkspaceService } from '../../workspace';
import type { AccessibleDocCacheStore } from '../stores/accessible-doc-cache';
import { prepareDatabaseSubdocsForSnapshotExport } from './database-subdoc-preload';
import type { DocsService } from './docs';

type FileSystemSink = {
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
  present(): Promise<void>;
};

type ExportProgress =
  | { phase: 'inventory'; discoveredDocs: number }
  | WorkspaceArchiveProgress;

type WorkspaceSnapshotStreamInput = {
  workspaceService: WorkspaceService;
  docsService: DocsService;
  accessibleDocCache: AccessibleDocCacheStore;
  signal?: AbortSignal;
  onProgress?: (progress: ExportProgress) => void;
};

type WorkspaceSnapshotDocumentWriter = Pick<
  WorkspaceYDocArchiveWriter,
  'appendFamily' | 'finishFamily'
>;

type DiskClaimIndex = {
  claim(id: string): Promise<boolean>;
};

export async function createDiskClaimIndexes(labels: string[]): Promise<{
  indexes: DiskClaimIndex[];
  dispose(): Promise<void>;
}> {
  const root = await navigator.storage.getDirectory();
  const directoryName = `.streamlain-export-index-${crypto.randomUUID()}`;
  const parent = await root.getDirectoryHandle(directoryName, {
    create: true,
  });
  try {
    const indexes = await Promise.all(
      labels.map(async label => {
        const directory = await parent.getDirectoryHandle(label, {
          create: true,
        });
        return {
          claim: async (id: string) => {
            const digest = await crypto.subtle.digest(
              'SHA-256',
              new TextEncoder().encode(id)
            );
            const filename = Array.from(new Uint8Array(digest), byte =>
              byte.toString(16).padStart(2, '0')
            ).join('');
            try {
              await directory.getFileHandle(filename);
              return false;
            } catch (error) {
              if (
                !(error instanceof DOMException) ||
                error.name !== 'NotFoundError'
              ) {
                throw error;
              }
            }
            await directory.getFileHandle(filename, { create: true });
            return true;
          },
        } satisfies DiskClaimIndex;
      })
    );
    return {
      indexes,
      dispose: () => root.removeEntry(directoryName, { recursive: true }),
    };
  } catch (error) {
    await root.removeEntry(directoryName, { recursive: true }).catch(() => {});
    throw error;
  }
}

async function createFileSystemSink(filename: string): Promise<FileSystemSink> {
  const picker = (
    globalThis as typeof globalThis & {
      showSaveFilePicker?: (options: {
        suggestedName: string;
        types: Array<{
          description: string;
          accept: Record<string, string[]>;
        }>;
      }) => Promise<FileSystemFileHandle>;
    }
  ).showSaveFilePicker;
  if (picker) {
    const handle = await picker({
      suggestedName: filename,
      types: [
        {
          description: `${BRAND_DISPLAY_NAME} workspace snapshot`,
          accept: { 'application/zip': ['.zip'] },
        },
      ],
    });
    const writable = await handle.createWritable();
    return {
      write: chunk => writable.write(chunk),
      close: () => writable.close(),
      abort: async () => {
        await writable.abort().catch(() => {});
      },
      present: async () => {},
    };
  }

  const root = await navigator.storage.getDirectory();
  const tempName = `.streamlain-export-${crypto.randomUUID()}.zip`;
  const handle = await root.getFileHandle(tempName, { create: true });
  const writable = await handle.createWritable();
  return {
    write: chunk => writable.write(chunk),
    close: () => writable.close(),
    abort: async () => {
      await writable.abort().catch(() => {});
      await root.removeEntry(tempName).catch(() => {});
    },
    present: async () => {
      const file = await handle.getFile();
      download(file, filename);
      await root.removeEntry(tempName);
    },
  };
}

export async function streamWorkspaceSnapshotDocuments(
  input: WorkspaceSnapshotStreamInput,
  writer: WorkspaceSnapshotDocumentWriter,
  sourceIndex: DiskClaimIndex
): Promise<number> {
  const workspace = input.workspaceService.workspace;
  let processed = 0;
  if (workspace.flavour !== 'local') {
    await input.accessibleDocCache.fetchAccessibleDocs(input.signal);
  }
  while (true) {
    input.signal?.throwIfAborted();
    const ids: string[] = [];
    for (const doc of input.docsService.list.docs$.value) {
      if (await sourceIndex.claim(doc.id)) ids.push(doc.id);
    }
    input.onProgress?.({
      phase: 'inventory',
      discoveredDocs: processed + ids.length,
    });

    for (const id of ids) {
      input.signal?.throwIfAborted();
      const initiallyHadWorkspaceLoadRequest = (
        await workspace.engine.doc.debugDocState(id)
      ).some(state => state.reasons.includes('request:workspace-doc-load'));
      workspace.engine.doc.addDoc(id, 'workspace-snapshot-export');
      const releasePriority = workspace.engine.doc.addPriority(id, 90);
      try {
        const doc = workspace.docCollection.getDoc(id);
        if (!doc) continue;
        if (!doc.ready) doc.load();
        await workspace.engine.doc.waitForDocReady(id, input.signal);
        const store = doc.getStore();
        if (!store) continue;
        try {
          await writer.appendFamily([store]);
          await prepareDatabaseSubdocsForSnapshotExport(
            [store],
            input.workspaceService,
            {
              mode: 'workspace',
              workspaceStream: {
                claimDocId: docId => sourceIndex.claim(docId),
                onStoreReady: subdoc => writer.appendFamily([subdoc]),
              },
            }
          );
        } finally {
          writer.finishFamily();
        }
      } finally {
        processed += 1;
        releasePriority();
        await workspace.engine.doc.releaseDoc(id, 'workspace-snapshot-export');
        if (!initiallyHadWorkspaceLoadRequest) {
          await workspace.engine.doc.releaseDoc(id, 'workspace-doc-load');
        }
      }
    }

    if (
      workspace.flavour === 'local' ||
      !input.accessibleDocCache.hasNextMetadataPage$.value.all
    ) {
      break;
    }
    await input.accessibleDocCache.fetchNextAccessibleDocsPage(
      'all',
      input.signal
    );
  }
  return processed;
}

export async function exportWorkspaceSnapshotStream(
  input: WorkspaceSnapshotStreamInput
): Promise<void> {
  const workspace = input.workspaceService.workspace;
  const sink = await createFileSystemSink(`${workspace.id}.bs.zip`);
  let claimIndexes;
  try {
    claimIndexes = await createDiskClaimIndexes(['sources', 'docs', 'blobs']);
  } catch (error) {
    await sink.abort();
    throw error;
  }
  const [sourceIndex, archiveDocIndex, blobIndex] = claimIndexes.indexes;
  const writer = new WorkspaceYDocArchiveWriter(
    workspace.docCollection,
    chunk => sink.write(chunk),
    {
      claimBlobId: id => blobIndex.claim(id),
      claimDocId: id => archiveDocIndex.claim(id),
      signal: input.signal,
      onProgress: input.onProgress,
    }
  );
  try {
    const processed = await streamWorkspaceSnapshotDocuments(
      input,
      writer,
      sourceIndex
    );
    if (processed === 0) {
      throw new Error('Snapshot export requires at least one document.');
    }
    await writer.close();
    await sink.close();
    await sink.present();
  } catch (error) {
    await sink.abort();
    throw error;
  } finally {
    await claimIndexes.dispose();
  }
}
